import MinusCircleOutlined from '@ant-design/icons/MinusCircleOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import { Page, Text, View } from '@react-pdf/renderer'
import { Button, DatePicker, Form, Input, message, Space } from 'antd'
import dayjs from 'dayjs'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { RecommendationsData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'pages/reporting/shared/pdfGeneration/utils/formatters'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useMemo } from 'react'

const requiredRecommendationsFields = ['recommendations']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(
    () => formData.recommendationsData,
    [formData.recommendationsData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page size='A4' style={PDFStyles.page} id='recommendations' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'recommendations', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Recommendations' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.recommendations && data.recommendations.length > 0 ? (
            data.recommendations.map((rec, index) => (
              <View key={index} style={{ marginBottom: 20 }}>
                {/* Recommendation Header */}
                <View style={{ flexDirection: 'row', marginBottom: 5 }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, width: 120 }}>
                    Recommendation ID
                  </Text>
                  <Text style={{ fontSize: 10 }}>{rec.recommendationId}</Text>
                </View>

                <View style={{ flexDirection: 'row', marginBottom: 5 }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, width: 120 }}>
                    Headline
                  </Text>
                  <Text style={{ fontSize: 10 }}>{rec.headline}</Text>
                </View>

                <View style={{ flexDirection: 'row', marginBottom: 5 }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, width: 120 }}>
                    Description
                  </Text>
                  <Text style={{ fontSize: 10, flex: 1 }}>
                    {rec.description}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', marginBottom: 5 }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, width: 120 }}>
                    Target Completion Date
                  </Text>
                  <Text style={{ fontSize: 10 }}>
                    {rec.targetCompletionDate
                      ? formatDate(dayjs(rec.targetCompletionDate))
                      : 'N/A'}
                  </Text>
                </View>

                <View style={{ flexDirection: 'row', marginBottom: 5 }}>
                  <Text style={{ fontSize: 10, fontWeight: 700, width: 120 }}>
                    AuthorName
                  </Text>
                  <Text style={{ fontSize: 10 }}>{rec.author}</Text>
                </View>

                {/* Add separator between recommendations */}
                {index < (data?.recommendations?.length ?? 0) - 1 && (
                  <View
                    style={{ borderBottom: '1 solid #E5E7EB', marginTop: 10 }}
                  />
                )}
              </View>
            ))
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<RecommendationsData>()
  const { formData, handleRecommendationsFinish } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: RecommendationsData) => {
      try {
        // Convert DatePicker moments to ISO strings
        const recommendations =
          values.recommendations?.map((rec) => ({
            ...rec,
            targetCompletionDate: rec.targetCompletionDate
              ? dayjs(rec.targetCompletionDate).toISOString()
              : '',
          })) || []

        await handleRecommendationsFinish(recommendations)
        message.success('Recommendations saved successfully!')
      } catch {
        message.error('Failed to save recommendations')
      }
    },
    [handleRecommendationsFinish]
  )

  const initialValues = React.useMemo(
    () => ({
      recommendations:
        formData.recommendationsData.recommendations?.map((rec) => ({
          ...rec,
          targetCompletionDate: rec.targetCompletionDate
            ? dayjs(rec.targetCompletionDate)
            : undefined,
        })) || [],
    }),
    [formData.recommendationsData]
  )

  return (
    <StyledForm<RecommendationsData>
      form={form}
      name='recommendationsForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.List name='recommendations'>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Space
                key={key}
                style={{ display: 'flex', marginBottom: 8 }}
                align='baseline'>
                <Form.Item
                  {...restField}
                  name={[name, 'recommendationId']}
                  label='Recommendation ID'
                  rules={[
                    { required: true, message: 'Missing recommendation ID' },
                  ]}>
                  <Input placeholder='REC-123456' />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'headline']}
                  label='Headline'
                  rules={[{ required: true, message: 'Missing headline' }]}>
                  <Input placeholder='Brief headline' />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'description']}
                  label='Description'
                  rules={[{ required: true, message: 'Missing description' }]}>
                  <Input.TextArea
                    placeholder='Detailed description'
                    rows={3}
                    style={{ width: 300 }}
                  />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'targetCompletionDate']}
                  label='Target Date'
                  rules={[{ required: true, message: 'Missing target date' }]}>
                  <DatePicker />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'author']}
                  label='Author'
                  rules={[{ required: true, message: 'Missing author' }]}>
                  <Input placeholder='Author name' />
                </Form.Item>

                <MinusCircleOutlined onClick={() => remove(name)} />
              </Space>
            ))}

            <Form.Item>
              <Button
                type='dashed'
                onClick={() => add()}
                block
                icon={<PlusOutlined />}>
                Add Recommendation
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Recommendations
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const RecommendationsSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'recommendations',
  description: 'Recommended actions based on findings',
  title: 'Recommendations',
  requiredFields: requiredRecommendationsFields,
}

export const RecommendationsSectionPDF = RenderPDF
